import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Button, CircularProgress, Typography } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import { ENTITY_LIST_QUERY } from '../../graphql/entityList'
import {
  ROLE_FEATURES_QUERY,
  UPDATE_FEATURE_FLAG_MUTATION
} from '../../graphql/featureFlag'
import { ROLES_QUERY } from '../../graphql/role'
import { getTranslate, lang } from '../../localization'
import { EntityList } from '../../types/entityList'
import {
  GetRoleFeaturesInput,
  RoleFeature,
  UpdateFeatureFlagInput
} from '../../types/featureFlag'
import { RoleBrief } from '../../types/role'
import FeatureBox from './featureBox'
import FeatureFilters from './filters'
import { AreRoleFeaturesDifferent } from './functions'
import useStyles from './styles/index.styles'

function FeatureFlag() {
  const classes = useStyles()
  const history = useHistory()
  const [autoCompleteForRole, setAutoCompleteForRele] = useState<any>(null)
  const [autoCompleteForEntity, setAutoCompleteForEntity] = useState<any>(null)
  const [roleFeaturesInput, setRoleFeaturesInput] =
    useState<GetRoleFeaturesInput>({ roleID: '' })
  const [updateFeatureFlagInput, setUpdateFeatureFlagInput] =
    useState<UpdateFeatureFlagInput>({
      roleID: '',
      featureIDs: []
    })

  const [initialFeatures, setInitialFeatures] = useState<RoleFeature[]>([])
  const [temporaryFeatures, setTemporaryFeatures] = useState<RoleFeature[]>([])
  const [filteredFeatures, setFilteredFeatures] = useState<RoleFeature[]>([])

  const { data: rolesData } = useQuery<{
    roles: RoleBrief[]
  }>(ROLES_QUERY, {
    variables: { filters: {} },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const [getEntityList, { data: entityListData }] = useLazyQuery<{
    entityList: EntityList[]
  }>(ENTITY_LIST_QUERY, {
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [updateFeature, { loading: loadingForUpdateFeature }] = useMutation<{}>(
    UPDATE_FEATURE_FLAG_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (data: any) => {
        toast.success(data.updateFeatureFlag.message)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const [getRoleFeatures, { loading: loadingForRoleFeatures }] = useLazyQuery<{
    roleFeatures: RoleFeature[]
  }>(ROLE_FEATURES_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const selectedFeatureIDs = res.roleFeatures.flatMap((rf) =>
        rf.features.filter((f) => f.selected).map((f) => f.featureID)
      )
      setUpdateFeatureFlagInput((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          featureIDs: [...(prev.featureIDs ?? []), ...selectedFeatureIDs]
        }
      })
      setTemporaryFeatures(res.roleFeatures)
      setInitialFeatures(res.roleFeatures)
    }
  })

  useEffect(() => {
    if (autoCompleteForRole && autoCompleteForRole.value)
      getEntityList({
        variables: {
          entityType: autoCompleteForRole.value
        }
      })
  }, [autoCompleteForRole])

  useEffect(() => {
    const selectedFeatureIDs = temporaryFeatures
      .flatMap((rf) => rf.features)
      .filter((f) => f.selected)
      .map((f) => f.featureID)

    setUpdateFeatureFlagInput((prev) => ({
      ...prev,
      featureIDs: selectedFeatureIDs
    }))
  }, [temporaryFeatures])

  useEffect(() => {
    if (
      (roleFeaturesInput?.roleID &&
        !roleFeaturesInput.entityType &&
        !roleFeaturesInput.entityCode) ||
      (roleFeaturesInput?.roleID &&
        roleFeaturesInput.entityType &&
        roleFeaturesInput.entityCode)
    ) {
      getRoleFeatures({ variables: { input: roleFeaturesInput } })
    }
  }, [
    roleFeaturesInput.roleID,
    roleFeaturesInput.entityType,
    roleFeaturesInput.entityCode
  ])

  const showUnsavedChanges = filteredFeatures.length > 0
  return (
    <div className={classes.main}>
      <div className={classes.root}>
        <PageHeader title={getTranslate('Feature Flag')} />
        <div className={classes.header}>
          <Typography variant="h3" gutterBottom>
            {getTranslate('Feature Flag')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              history.push(
                `/${lang}/access-management/feature-flag/bulk-update`
              )
            }}>
            <Typography className={classes.whiteText}>
              {getTranslate('Features Flag Bulk Update')}
            </Typography>
          </Button>
        </div>
        <FeatureFilters
          autoCompleteForRole={autoCompleteForRole}
          setAutoCompleteForRele={setAutoCompleteForRele}
          autoCompleteForEntity={autoCompleteForEntity}
          setAutoCompleteForEntity={setAutoCompleteForEntity}
          roleFeaturesInput={roleFeaturesInput}
          setRoleFeaturesInput={setRoleFeaturesInput}
          updateFeatureFlagInput={updateFeatureFlagInput}
          setUpdateFeatureFlagInput={setUpdateFeatureFlagInput}
          roles={rolesData?.roles || []}
          entityList={entityListData?.entityList || []}
          temporaryFeatures={temporaryFeatures}
          setFilteredFeatures={setFilteredFeatures}
        />
        <Typography className={classes.subtitle}>
          {getTranslate('Features List')}
        </Typography>
        <div className={classes.content}>
          {loadingForRoleFeatures ? (
            <div className={classes.loadingContainer}>
              <CircularProgress size={36} />
            </div>
          ) : (
            filteredFeatures.map((roleFeature, index) => (
              <FeatureBox
                key={index}
                featuresGroup={roleFeature}
                temporaryFeatures={temporaryFeatures}
                setTemporaryFeatures={setTemporaryFeatures}
              />
            ))
          )}
        </div>
      </div>
      {showUnsavedChanges && (
        <div className={classes.footer}>
          {AreRoleFeaturesDifferent(initialFeatures, temporaryFeatures) ? (
            <div>
              <Typography className={classes.title}>
                {getTranslate('Unsaved Changes')}
              </Typography>
              <Typography className={classes.subTitle}>
                {getTranslate(
                  'You have pending changes that have not been saved'
                )}
              </Typography>
            </div>
          ) : (
            <div></div>
          )}

          <div className={classes.btnContainer}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.cancelBtn}
              onClick={() => {
                setTemporaryFeatures(initialFeatures)
              }}>
              <Typography className={classes.redText}>
                {getTranslate('Cancel')}
              </Typography>
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                updateFeature({ variables: { input: updateFeatureFlagInput } })
              }}
              disabled={
                !autoCompleteForRole ||
                filteredFeatures.length < 1 ||
                loadingForUpdateFeature ||
                !AreRoleFeaturesDifferent(initialFeatures, temporaryFeatures)
              }>
              <Typography className={classes.whiteText}>
                {getTranslate('Save Changes')}
              </Typography>
              {loadingForUpdateFeature && (
                <CircularProgress
                  className={classes.circularProgress}
                  size={16}
                  color="primary"
                />
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default FeatureFlag
